import { useState, useCallback } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { ApolloError } from 'apollo-client'
import { useDeepEffect } from '@retailer-platform/shared-common'
import {
  queryStoreGroups,
  type StoreGroup,
} from 'domains/account-management/api/store-groups/utils/queryStoreGroups'

interface HookResult {
  data: StoreGroup[]
  loading: boolean
  error: Error
}

/**
 * Queries for multiple calls to fetch store groups for the given
 * retailer ids. This will combine the results into a single response.
 *
 * @param ids the ids of the retailers
 */
export const useStoreGroups = (ids: string[]): HookResult => {
  const client = useApolloClient()
  const [results, setResults] = useState<HookResult>({
    data: null,
    loading: false,
    error: null,
  })

  const query = useCallback(
    async (ids: string[]) => {
      /**
       * Note that we use promises instead of hooks here. this is because
       * we don't know beforehand how many calls we need to do, and
       * that would violate rules of hooks (e.g. all runs should contain
       * the same hooks in the same order)
       */
      const queries = ids.map(id => queryStoreGroups(client, id))

      setResults({ data: null, error: null, loading: true })

      const batchedResult = await Promise.all(queries)

      const accumulatedResults = batchedResult.reduce(
        (acc, result) => {
          if (result.errors?.length) {
            acc.errors = acc.errors.concat(result.errors)
          }

          if (result.data) {
            acc.data = acc.data.concat(result.data ?? [])
          }

          return acc
        },
        { errors: [], data: [] }
      )

      if (accumulatedResults.errors.length) {
        accumulatedResults.data = null
      } else {
        accumulatedResults.errors = null
      }

      const error = accumulatedResults.errors
        ? new ApolloError({
            graphQLErrors: accumulatedResults.errors,
            errorMessage: 'Unable to fetch store groups',
          })
        : null
      return setResults({ data: accumulatedResults.data, error, loading: false })
    },
    [client]
  )

  useDeepEffect(() => {
    query(ids)
  }, [ids, query])

  return results
}
